/**
 * Suche im Mobil-Header: EIN Tipp bis zur offenen Tastatur.
 *
 * Zwei Ausloeser laufen hier durch:
 *  - das Such-Icon der Kopfleiste (Horizons Bindung
 *    `on:click="#search-modal/showDialog"` oeffnet das Modal selbst), und
 *  - die Suchleiste in der Menue-Schublade (`.custom-drawer-search`), fuer die
 *    dieses Skript Schublade-zu und Modal-auf selbst orchestriert (Historie im
 *    Kommentar weiter unten).
 *
 * DAS Kernproblem (Nutzer-Meldung 2026-08-27, zweiter Anlauf): Auf dem
 * Telefon brauchte es zwei Tipps, bis man tippen konnte. iOS oeffnet die
 * Bildschirmtastatur nur, wenn ein Eingabefeld SYNCHRON innerhalb der
 * Nutzergeste fokussiert wird. Horizons `dialog.js` oeffnet das Modal aber
 * erst im naechsten Frame (`requestAnimationFrame` -> `showModal()`), und ein
 * Feld in einem geschlossenen <dialog> ist nicht fokussierbar — jeder Fokus
 * kam also zu spaet, die Tastatur blieb zu, der zweite Tipp aufs Feld musste
 * sie holen.
 *
 * Loesung ist die Tastatur-Bruecke: Im Klick-Handler wird synchron ein
 * unsichtbares Suchfeld fokussiert (die Tastatur geht auf, noch in der Geste),
 * und sobald der Dialog offen ist, wandert der Fokus aufs echte Feld — ein
 * Wechsel von Eingabefeld zu Eingabefeld haelt die Tastatur offen. Die
 * Bruecke laeuft nur auf Touch-Geraeten (`pointer: coarse`); am Desktop
 * fokussiert das Modal selbst korrekt.
 *
 * Delegation am `document` und kein eigenes Element: Die Schublade wird bei
 * Aenderungen neu gemorpht, ein Listener am Knopf waere danach weg.
 */

const AUSLOESER = '.custom-drawer-search, [on\\:click="#search-modal/showDialog"]';

/** @param {HTMLElement} modal */
function echtesFeld(modal) {
  const dialog = modal.querySelector('dialog');
  if (!dialog || !dialog.open) return null;
  const feld = dialog.querySelector('input[type="search"], #cmdk-input');
  return feld instanceof HTMLElement ? feld : null;
}

/**
 * Unsichtbares Suchfeld, das die Tastatur in der Nutzergeste oeffnet.
 * `font-size: 16px` verhindert den iOS-Auto-Zoom beim Fokus.
 */
function tastaturBruecke() {
  const geist = document.createElement('input');
  geist.type = 'search';
  geist.setAttribute('aria-hidden', 'true');
  geist.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;';
  document.body.appendChild(geist);
  geist.focus({ preventScroll: true });
  return {
    /** @param {HTMLElement} feld */
    uebergeben(feld) {
      feld.focus({ preventScroll: true });
      geist.remove();
    },
    abbrechen() {
      geist.remove();
    },
  };
}

document.addEventListener('click', (event) => {
  const ziel = event.target;
  if (!(ziel instanceof Element)) return;

  const ausloeser = ziel.closest(AUSLOESER);
  if (!ausloeser) return;

  const modal = document.getElementById('search-modal');
  if (!modal) return;

  const istMenueLeiste = ausloeser.classList.contains('custom-drawer-search');
  if (istMenueLeiste) event.preventDefault();

  const bruecke = window.matchMedia('(pointer: coarse)').matches ? tastaturBruecke() : null;

  if (istMenueLeiste) {
    // Vorher trug die Leiste Horizons deklarative Bindung — das oeffnete die
    // Suche, liess die Schublade aber offen darunter stehen. Beides gehoert in
    // eine Hand: erst zu, dann im naechsten Frame auf. `close()` der
    // Komponente und nicht `details.open = false`: Sie raeumt Seitensperre,
    // ARIA-Zustand und offene Untermenues mit auf. Der eine Frame Abstand:
    // Das Modal setzt seine eigene Seitensperre in einem requestAnimationFrame
    // — oeffnet es im selben Frame, in dem die Schublade ihre Sperre freigibt,
    // gewinnt die Freigabe und der Hintergrund scrollt.
    const schublade = ausloeser.closest('header-drawer');
    if (schublade && typeof (/** @type {any} */ (schublade).close) === 'function') {
      /** @type {any} */ (schublade).close();
    }
    requestAnimationFrame(() => {
      /** @type {any} */ (modal).showDialog?.();
    });
  }
  // Beim Such-Icon der Kopfleiste oeffnet Horizons eigene Bindung das Modal.

  // Fokus aufs echte Feld weiterreichen, sobald der Dialog offen ist
  // (Polling per Frame, Deckel ~1,5s — danach Bruecke aufgeben).
  let versuche = 0;
  const uebergabe = () => {
    const feld = echtesFeld(modal);
    if (feld) {
      if (bruecke) bruecke.uebergeben(feld);
      else if (document.activeElement !== feld) feld.focus({ preventScroll: true });

      // Fokus-Sicherung fuer den Menue-Weg: `header-drawer.close()` raeumt am
      // Ende seiner Schliess-Animation den Fokus auf (`removeTrapFocus`), ein
      // paar hundert Millisekunden NACH dem Oeffnen der Suche. Ohne diese
      // Zeile kann der Fokus dem Kunden mitten im Tippen aus dem Suchfeld
      // gezogen werden.
      if (istMenueLeiste) {
        setTimeout(() => {
          const dialog = modal.querySelector('dialog');
          const f = echtesFeld(modal);
          if (dialog && dialog.open && f && !dialog.contains(document.activeElement)) {
            f.focus({ preventScroll: true });
          }
        }, 500);
      }
      return;
    }
    if (++versuche < 90) requestAnimationFrame(uebergabe);
    else if (bruecke) bruecke.abbrechen();
  };
  requestAnimationFrame(uebergabe);
});
